import { useStateRef } from '@beesoft/common';
import clsx from 'clsx';
import type { ReactElement, Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { forEachAdd } from '../common-functions';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';

interface ElementNavigatorProps {
  id?: string;
  activeIndex?: number;
  leftArrow?: ReactElement;
  rightArrow?: ReactElement;
  elementNavigatorStyles?: string;
  scrollerStyles?: string;
  elementNavigatorContainerStyles?: string;
  children: ReactElement | Array<ReactElement>;
}

export interface ElementNavigatorRef {
  setScrollTab: (index: number) => void;
}

const ElementNavigator = (
  {
    id,
    elementNavigatorStyles,
    scrollerStyles = 'odin-shrink',
    elementNavigatorContainerStyles = 'odin-flex odin-shrink odin-flex-row odin-items-center',
    leftArrow = <OdinIcon icon="ChevronLeft" size={OdinIconSize.Medium} />,
    rightArrow = <OdinIcon icon="ChevronRight" size={OdinIconSize.Medium} />,
    children,
  }: ElementNavigatorProps,
  ref: Ref<ElementNavigatorRef>
) => {
  const [scrollElementIndex, setScrollElementIndex, scrollElementIndexRef] = useStateRef(0);
  const elementNavigationRef = useRef<HTMLDivElement | undefined>(undefined);
  const intersectionObserver = useRef<IntersectionObserver | undefined>(undefined);
  const elements = useRef<Array<Element> | undefined>(undefined);
  const margin = useRef(0);

  const [scrollerRightEnabled, setScrollerRightEnabled] = useState(true);

  const [scrollerVisible, setScrollerVisible] = useState(true);

  useEffect(() => {
    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (elements.current && elementNavigationRef.current) {
      const left =
        scrollElementIndex > 0
          ? forEachAdd(
              elements.current,
              (item) => (item ? item.getBoundingClientRect().width + margin.current : 0),
              0,
              scrollElementIndex - 1
            )
          : 0;

      elementNavigationRef.current.scrollTo({
        left,
      });
    }
  }, [scrollElementIndex]);

  const observerCallback = useCallback((entries: Array<IntersectionObserverEntry>) => {
    const allFieldVisible = entries.every((entry) => entry.intersectionRatio === 1);
    setScrollerVisible(!allFieldVisible);

    if (!allFieldVisible) {
      const lastEntry = entries[entries.length - 1];
      //if elementNavigator's width is less than the last item's width,
      // then we can't fit the item in full
      //and should disable scroller
      const itemWidth = lastEntry.boundingClientRect.width;
      const navigationWidth = elementNavigationRef.current?.clientWidth ?? 0;
      const fullInView = lastEntry.intersectionRatio === 1 || navigationWidth < itemWidth;
      if (lastEntry.isIntersecting && fullInView) {
        setScrollerRightEnabled(false);
      } else {
        setScrollerRightEnabled(true);
      }
    }
  }, []);

  const onElementNavigationCreated = (navigationElement: HTMLDivElement) => {
    elementNavigationRef.current = navigationElement;

    intersectionObserver.current?.disconnect();
    intersectionObserver.current = new IntersectionObserver(observerCallback);

    elements.current = Array.from(navigationElement.children);
    margin.current = 0;

    const firstItem = elements.current?.[0];
    if (firstItem) {
      const marginRight = parseInt(getComputedStyle(firstItem).marginRight);
      const marginLeft = parseInt(getComputedStyle(firstItem).marginLeft);
      margin.current = marginRight + marginLeft;
    }
    for (let i = 0, length = elements.current.length; i < length; i++) {
      const element = elements.current[i];
      if (element) {
        intersectionObserver.current?.observe(element);
      }
    }
  };

  const setScrollTab = (index: number) => {
    setScrollElementIndex(index);
  };

  const movePreviousElement = useCallback(() => {
    setScrollElementIndex(scrollElementIndexRef.current - 1);
  }, []);

  const moveNextElement = useCallback(() => {
    setScrollElementIndex(scrollElementIndexRef.current + 1);
  }, []);

  useImperativeHandle(ref, () => ({
    setScrollTab,
  }));

  const previousStyles = clsx('odin-mr-2 odin-cursor-pointer', {
    'odin-pointer-events-none odin-text-gray-4': !(scrollElementIndex > 0),
  });

  const nextStyles = clsx('odin-cursor-pointer', {
    'odin-pointer-events-none odin-text-gray-4': !scrollerRightEnabled,
  });

  const navigationStyles = clsx(
    'odin-list-none odin-bg-mono-1 odin-grow odin-flex odin-flex-row odin-overflow-x-scroll no-scrollbar fix-ul',
    {
      elementNavigatorStyles: elementNavigatorStyles,
      'odin-border-r odin-border-solid odin-border-gray-4': scrollerVisible,
    }
  );

  return (
    <div id={id} className={elementNavigatorContainerStyles}>
      <div
        ref={(element) => {
          if (element) onElementNavigationCreated(element);
        }}
        className={navigationStyles}
      >
        {children}
      </div>
      {scrollerVisible && (
        <div className={scrollerStyles}>
          <div className="odin-ml-2 odin-flex">
            <div className={previousStyles} onClick={movePreviousElement}>
              {leftArrow}
            </div>
            <div className={nextStyles} onClick={moveNextElement}>
              {rightArrow}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default forwardRef(ElementNavigator);
